import {regNavn} from "felles/tjenester/tredjepartsTjenestenavn";
import {l} from "types/typescriptdefinisjoner_lederplan";
import _ from "lodash";
import {gettextCatalog} from "angular-gettext";
import {MeldingTilBruker, Varighet} from "felles/tjenester/MeldingTilBruker";
import {Filer} from "felles/tjenester/Filer";
import {Logger} from "../../../felles/infrastruktur/Logging";
import {Dialog} from "../Dialog";
import {ILogService, IQService} from "angular";
import {FilValidering} from "web/tjenester/FilValidering";
import {SaksdokumentDatatjenesteForAjs} from "web/nedgraderteNg2Tjenester";
import {HttpErrorResponse, HttpEvent, HttpEventType, HttpProgressEvent, HttpResponse} from "@angular/common/http";
import {SaksdokumentModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {kopierFraNg2TilAjs} from "felles-ng2/modell/ajsNg2ModellBroer";
import {catchError, concat, EMPTY, firstValueFrom, Observable, Subject, takeUntil} from "rxjs";
import {SaksdokumentDatatjeneste} from "felles-ng2/tjenester/datalager/SaksdokumentDatatjeneste";
import {tap} from "rxjs/operators";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {tomFunksjon} from "felles-ng2/støttefunksjoner/funksjoner";
import {ng2RegNavn} from "web/nedgraderteNg2TjenesterRegisternavn";
import {erIkkeTomListe} from "felles-ng2/støttefunksjoner/lister";
import {IPromise} from "q";
import {Feilhåndteringsstrategi} from "felles-ng2/tjenester/datalager/datatjeneste";
import {erHttpErrorResponse} from "felles-ng2/infrastruktur/http/httpErrorResponseTyper";
import {erStreng} from "felles-ng2/støttefunksjoner/strenger";

type FilOgMuligEksisterende = { fil: File; eksisterende: SaksdokumentModell | null; }

export class SakWebRessurs {

    static regNavn = "SakWebRessurs";

    static forInjisering() {
        return [
            regNavn.ng.$log,
            regNavn.ng.$q,
            MeldingTilBruker.regNavn,
            Filer.regNavn,
            FilValidering.regNavn,
            Logger.regNavn,
            Dialog.regNavn,
            regNavn.gettext.gettextCatalog,
            ng2RegNavn.SaksdokumentDatatjenesteForAjs,
            SakWebRessurs
        ]
    }

    constructor(public $log: ILogService,
                public $q: IQService,
                public meldingTilBruker: MeldingTilBruker,
                public filer: Filer,
                private filValidering: FilValidering,
                public logger: Logger,
                private dialog: Dialog,
                public gettextCatalog: gettextCatalog,
                public saksdokumentDatatjenesteForAjs: SaksdokumentDatatjenesteForAjs) {
    }

    saksdokumentopplastinger = {};

    //offentlige metoder

    /**
     * Laster opp angitte filer under angitt sak.
     * Dersom det allerede eksisterer et dokument med samme filnavn blir bruker spurt om vedkommene vil registrere det som en ny versjon.
     * Filer vil bli lastet opp sekvensielt. Dette da serversiden ikke takler
     * parallelle kall til sak/opprettDokument (pga hibernate List oppførsel)
     */
    registrerDokumenter(sakId: string, filer: File[], vedOk: (nyttDokument: l.SaksdokumentModell) => void) {
        this.saksdokumentDatatjenesteForAjs.tjeneste()
          //hent eksisterende saksdokumenter fra datalager
          .then(saksdokumentDatatjeneste => firstValueFrom(saksdokumentDatatjeneste.finnTilknyttetSakFraLokallager(sakId)))
          //valider filer som ønskes lastet opp
          .then(eksisterendeSaksdok => this.$q.all([eksisterendeSaksdok, this.filValidering.validerFilerFørOpplastingOgVisDialogHvisFeilet(filer)]))
          //identifiser reg av ny versjon versus nytt dokument
          .then(([eksisterendeSaksdok, filerErGyldige]) => {
              // Avslutt opplasting, en eller flere filer er ugyldige
              if (!filerErGyldige) return;

              const kandidater = grupperFilerIOppdatertOgNy(filer, eksisterendeSaksdok);
              if (erIkkeTomListe(kandidater.oppdaterte)) {
                  this.bekreftRegistrerNyeVersjoner(kandidater.oppdaterte)
                    .then(() => {
                        kandidater.oppdaterte.forEach(oppdatert =>
                          this.opprettNyDokumentVersjon(sakId, oppdatert.eksisterende.dokumentId, oppdatert.fil, vedOk));
                    });
              }

              if (erIkkeTomListe(kandidater.nye))
                  this.opprettDokumenter(sakId, kandidater.nye.map(ny => ny.fil));
          })
    }

    /**
     * Laster opp angitte filer under angitt sak.
     * Filer vil bli lastet opp sekvensielt. Dette da serversiden ikke takler
     * parallelle kall til sak/opprettDokument (pga hibernate List oppførsel)
     */
    opprettDokumenter(sakId: string, filer: File[]) {
        var opplastinger = _(filer).map(fil => Filopplasting.opprettDokument(fil, sakId, tomFunksjon, this)).value();

        this.saksdokumentDatatjenesteForAjs.tjeneste().then(saksdokumentDatatjeneste =>
          concat(...(opplastinger.map(opplasting => opplasting.lastOpp(saksdokumentDatatjeneste))))
            .subscribe(dokumenter => {
            }));
    }

    /**
     * Laster opp angitte filer under angitt sak.
     * Filer vil bli lastet opp sekvensielt. Dette da serversiden ikke takler
     * parallelle kall til sak/opprettDokument (pga hibernate List oppførsel)
     */
    opprettNyDokumentVersjon(sakId: string, dokumentId: string, fil: File, vedOk: (nyttDokument: l.SaksdokumentModell) => void) {
        var opplasting = Filopplasting.opprettDokumentVersjon(fil, sakId, dokumentId, vedOk, this);
        this.saksdokumentDatatjenesteForAjs.tjeneste().then(saksdokumentDatatjeneste =>
          opplasting.lastOpp(saksdokumentDatatjeneste).subscribe(dokumenter => {
          }));
    }

    finnOpplastinger(sakId: string): Filopplasting[] {
        return this.saksdokumentopplastinger[sakId];
    }

    //hjelpemetoder

    private bekreftRegistrerNyeVersjoner(oppdaterte: FilOgMuligEksisterende[]): IPromise<any> {
        const filnavn = oppdaterte.map(oppdatert => `<li>${oppdatert.eksisterende.filnavn}</li>`).join('');

        ///dialoginnhold for dialog ved opplasting av filsom allerede finnes i sak
        const dialogInnhold = this.gettextCatalog.getString('Følgende dokumenter eksisterer allerede, vil du registrere disse som nye versjoner?');

        return this.dialog.confirm(
          ///dialogtittel for dialog ved opplasting av filsom allerede finnes i sak
          this.gettextCatalog.getString('Dokumentregistrering'),
          `<p>${dialogInnhold}</p> <ul>${filnavn}</ul>`,
          ///ok-knapp i dialog ved opplasting av filsom allerede finnes i sak
          this.gettextCatalog.getString('Registrer'),
          ///avbryt-knapp i dialog ved opplasting av filsom allerede finnes i sak
          this.gettextCatalog.getString('Avbryt'));
    }

    registrerOpplasting(opplasting: Filopplasting) {
        if (!this.saksdokumentopplastinger[opplasting.sakId]) this.saksdokumentopplastinger[opplasting.sakId] = [];
        this.saksdokumentopplastinger[opplasting.sakId].push(opplasting);
    }

    fjernOpplasting(opplasting: Filopplasting) {
        if (!this.saksdokumentopplastinger[opplasting.sakId]) return;
        _.remove(this.saksdokumentopplastinger[opplasting.sakId], o => o === opplasting);
    }

    informerBrukerOmOpplastingAvbruttAvZscalar(opplasting: Filopplasting) {
        return this.dialog.alert(
          this.gettextCatalog.getString('Feil under opplasting av {{filnavn}}', {filnavn: opplasting.fil.name}),
          this.gettextCatalog.getString('Din organisasjon benytter et system (Zscaler) for å overvåke/begrense informasjonsdeling. Kontakt din IT-ansvarlig for informasjon om opplasting av filer.'));
    }
}

function grupperFilerIOppdatertOgNy(filer: File[], eksisterendeSaksdok: SaksdokumentModell[]) {
    return filer
      .map(fil => ({
          fil: fil,
          eksisterende: eksisterendeSaksdok.find(eksiterende => eksiterende.filnavn === fil.name) ?? null
      } as FilOgMuligEksisterende))
      .reduce((gruppert, opplasting) => {
          (erIkkeNil(opplasting.eksisterende) ? gruppert.oppdaterte : gruppert.nye).push(opplasting)
          return gruppert;
      }, {oppdaterte: [] as FilOgMuligEksisterende[], nye: [] as FilOgMuligEksisterende[]});
}

export class Filopplasting {
    progresjon = 0;
    avbrutt = false;
    terminer = new Subject<void>();
    formatikon = "lederplan-ikon-pdf";
    formatnavn = "pdf";
    viserDetaljer = false;
    mimetype: string;

    constructor(public fil: File, public sakId: string, private dokumentId: string, private vedOk: (nyttDokument: l.SaksdokumentModell) => void, private sakWebRessurs: SakWebRessurs) {
        sakWebRessurs.registrerOpplasting(this);

        this.mimetype = fil.type || sakWebRessurs.filer.finnMimetypeBasertPåFilendelse(fil);
        // Se #282
        if (_.isEmpty(this.mimetype)) {
            this.avbryt();
            sakWebRessurs.meldingTilBruker.feilmelding(`${sakWebRessurs.gettextCatalog.getString('Filen')}'${fil.name}' ${sakWebRessurs.gettextCatalog.getString('kunne ikke lastes opp pga. manglende MIME-type.')}`);
            sakWebRessurs.logger.info(`Filopplasting mangler MIME. Filendelse: ${fil.name.split('.')[1]}`);
        }
    }

    static opprettDokument(fil: File, sakId: string, vedOk: (nyttDokument: l.SaksdokumentModell) => void, sakWebRessurs: SakWebRessurs) {
        return new Filopplasting(fil, sakId, null, vedOk, sakWebRessurs);
    }

    static opprettDokumentVersjon(fil: File, sakId: string, dokumentId: string, vedOk: (nyttDokument: l.SaksdokumentModell) => void, sakWebRessurs: SakWebRessurs) {
        return new Filopplasting(fil, sakId, dokumentId, vedOk, sakWebRessurs);
    }

    lastOpp(saksdokumentDatatjeneste: SaksdokumentDatatjeneste): Observable<HttpEvent<SaksdokumentModell>> {
        return (erIkkeNil(this.dokumentId) ?
          saksdokumentDatatjeneste.registrerSaksdokumentVersjon(this.sakId, this.dokumentId, this.fil) :
          saksdokumentDatatjeneste.registrerSaksdokument(this.sakId, this.fil, Feilhåndteringsstrategi.propagerSomError))
          .pipe(
            takeUntil(this.terminer),
            catchError((e: HttpErrorResponse) => {
                if (erHttpErrorResponse(e)) {
                    if (e.status == 403 && erStreng(e.error) && e.error.includes('zscaler')) {
                        this.sakWebRessurs.informerBrukerOmOpplastingAvbruttAvZscalar(this)
                        this.sakWebRessurs.fjernOpplasting(this);
                        return EMPTY;
                    }
                }

                this.sakWebRessurs.$log.error(`Feil under opplasting av saksdokument, e: ${angular.toJson(e)}, kontekst: ${this.errorLogInfo()}`); //vi har i loggen fått meldinger om feilet opplasting uten at vi har kunnet ident årsak, logger derfor exception her
                let melding = this.sakWebRessurs.gettextCatalog.getString('Feil under opplasting av {{filnavn}}', {filnavn: this.fil.name});
                this.sakWebRessurs.meldingTilBruker.feilmelding(melding, Varighet.timeout, null, false);
                this.sakWebRessurs.fjernOpplasting(this);
                return EMPTY;
            }),
            tap(event => {
                if (event.type == HttpEventType.UploadProgress) {
                    const progresjon = event as HttpProgressEvent;
                    this.progresjon = Math.floor(100.0 * progresjon.loaded / progresjon.total)
                }
                if (event.type == HttpEventType.Response) {
                    const respons = event as HttpResponse<SaksdokumentModell>;
                    this.sakWebRessurs.fjernOpplasting(this);
                    this.vedOk(kopierFraNg2TilAjs(respons.body));
                }
            }));
    }

    avbryt() {
        if (this.avbrutt) return;
        this.avbrutt = true;
        this.terminer.next();
        this.sakWebRessurs.fjernOpplasting(this);
    }

    private errorLogInfo() {
        return angular.toJson({
            fileIkkeNull: this.fil != null,
            stoerrelse: this.fil?.size,
            type: this.mimetype,
            filnavn: this.fil?.name
        });
    }

}
